import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { stateNameFromCode, validateGstin } from '@bits/domain';
import type { GstinLookupResult } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export type GstinDerived = {
  gstin: string;
  pan: string;
  stateCode: string;
  stateName: string | null;
};

/** What a GSTIN says about itself, with no network call: null until it checks out. */
export function deriveFromGstin(raw: string | undefined): GstinDerived | null {
  const r = validateGstin(raw ?? '');
  if (!r.ok) return null;
  return {
    gstin: r.gstin,
    pan: r.pan,
    stateCode: r.stateCode,
    stateName: stateNameFromCode(r.stateCode),
  };
}

export function gstinHint(raw: string | undefined): string | undefined {
  const s = (raw ?? '').trim();
  if (!s) return undefined;
  const r = validateGstin(s);
  if (r.ok) {
    const name = stateNameFromCode(r.stateCode);
    return `State ${r.stateCode}${name ? ` · ${name}` : ''} · PAN ${r.pan}`;
  }
  if (r.reason === 'wrong_length') return `${s.length} of 15 characters`;
  if (r.reason === 'bad_checksum') return 'Check digit does not match. Re-check the number.';
  return 'Not a valid GSTIN format.';
}

export function placeOfSupplyHint(value: string | undefined): string {
  const name = stateNameFromCode(value);
  if (name) return name;
  return value?.trim()
    ? 'Use the two-digit GST state code, e.g. 29 for Karnataka.'
    : 'Two-digit GST state code. Filled in from the GSTIN when one is entered.';
}

type AutoFillField = 'placeOfSupplyState' | 'pan';

type AutoFillBinding = {
  getValue: (field: AutoFillField) => string | undefined;
  setValue: (field: AutoFillField, value: string) => void;
};

/**
 * Keeps place of supply and PAN in step with a valid GSTIN. A value the user
 * typed themselves is never overwritten; only an empty field, or one this hook
 * filled earlier, follows the GSTIN.
 */
export function useGstinAutoFill(
  gstin: string | undefined,
  binding: AutoFillBinding,
): GstinDerived | null {
  const derived = React.useMemo(() => deriveFromGstin(gstin), [gstin]);
  const bindingRef = React.useRef(binding);
  bindingRef.current = binding;
  const lastFilled = React.useRef<{ stateCode: string; pan: string } | null>(null);

  React.useEffect(() => {
    if (!derived) return;
    const { getValue, setValue } = bindingRef.current;
    const prev = lastFilled.current;
    const place = (getValue('placeOfSupplyState') ?? '').trim();
    if (!place || place === prev?.stateCode) setValue('placeOfSupplyState', derived.stateCode);
    const pan = (getValue('pan') ?? '').trim();
    if (!pan || pan === prev?.pan) setValue('pan', derived.pan);
    lastFilled.current = { stateCode: derived.stateCode, pan: derived.pan };
  }, [derived]);

  return derived;
}

export type GstinLookupState = {
  available: boolean;
  pending: boolean;
  result: GstinLookupResult | null;
  error: string | null;
  run: (gstin: string) => void;
  reset: () => void;
};

/** Server-backed lookup; `apply` receives the fetched details to push into the form. */
export function useGstinLookup(apply: (result: GstinLookupResult) => void): GstinLookupState {
  const status = useQuery({
    queryKey: ['gstin-lookup', 'status'],
    queryFn: () => api.gstinLookupStatus(),
    staleTime: Infinity,
  });
  const applyRef = React.useRef(apply);
  applyRef.current = apply;
  const mutation = useMutation({
    mutationFn: (gstin: string) => api.lookupGstin(gstin),
    onSuccess: (result) => applyRef.current(result),
  });
  return {
    available: status.data?.available ?? false,
    pending: mutation.isPending,
    result: mutation.data ?? null,
    error: mutation.error ? toMessage(mutation.error) : null,
    run: (gstin) => mutation.mutate(gstin),
    reset: () => mutation.reset(),
  };
}

export function GstinLookupButton({
  lookup,
  gstin,
}: {
  lookup: GstinLookupState;
  gstin: string | undefined;
}): JSX.Element | null {
  if (!lookup.available) return null;
  const valid = deriveFromGstin(gstin) !== null;
  return (
    <Button
      type="button"
      variant="outline"
      disabled={!valid || lookup.pending}
      onClick={() => lookup.run(gstin ?? '')}
      title={
        valid
          ? 'Fetch the registered details for this GSTIN'
          : 'Enter a valid 15-character GSTIN first'
      }
    >
      {lookup.pending ? 'Looking up…' : 'Look up'}
    </Button>
  );
}

export function GstinLookupNote({ lookup }: { lookup: GstinLookupState }): JSX.Element | null {
  if (lookup.error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>GSTIN lookup failed</AlertTitle>
        <AlertDescription>{lookup.error}</AlertDescription>
      </Alert>
    );
  }
  const r = lookup.result;
  if (!r) return null;
  const active = r.registrationStatus.toLowerCase() === 'active';
  const facts = [r.legalName, r.taxpayerType, r.constitution].filter(Boolean).join(' · ');
  return (
    <Alert variant={active ? 'success' : 'destructive'}>
      <AlertTitle>
        {active ? 'Filled from GST records' : `GST registration is ${r.registrationStatus}`}
      </AlertTitle>
      <AlertDescription>
        {facts}
        {r.registrationDate ? ` · registered ${r.registrationDate}` : ''}. Check the details before
        saving.
      </AlertDescription>
    </Alert>
  );
}
